import { useCallback, useEffect, useState } from "react";
import { useShowSnackbar } from "hooks/useShowSnackbar";
import { useNavigate } from "hooks/useCustomNavigation";
import { useCartContext } from "context/cart-context";
import { INTERNAL_PATHS } from "routes/paths";
import {
  applyPoints,
  checkout as checkoutRequest,
  getCheckoutData as getCheckoutDataRequest,
  removePoints
} from "./requests";
import { FEEDBACK_MESSAGES, INITIAL_CHECKOUT_CONTEXT_STATE } from "./constants";
import { useAppContext } from "context/app-context";
import { mountCustomError } from "utils/format";
import { useLocation } from "react-router";
import { setRiskUser } from "services/sardine";

export const useCheckoutData = ({ device }) => {
  const {
    sessionInfo: { userID }
  } = useAppContext();

  useEffect(() => {
    if (userID) setRiskUser(userID);
  }, [userID]);
  const { emptyCart } = useCartContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkoutData, setCheckoutData] = useState(
    INITIAL_CHECKOUT_CONTEXT_STATE.checkoutData
  );
  const [isLoading, setIsLoading] = useState(false);
  const [disableSubmitPayment, setDisableSubmitPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState("");
  const { addNotification } = useShowSnackbar();

  const getCheckoutData = useCallback(async () => {
    setIsLoading(true);
    // Ensure we only remove points if not on secondary checkout
    const removePoints =
      !!location.pathname !== INTERNAL_PATHS.SECONDARY_CHECKOUT;
    try {
      const response = await getCheckoutDataRequest(removePoints);
      setCheckoutData(response.data);
    } catch (err) {
      if (window.location.pathname !== INTERNAL_PATHS.SECONDARY_CHECKOUT) {
        return;
      }
      addNotification({
        message: FEEDBACK_MESSAGES.CHECKOUT_FETCH_ERROR,
        variant: "error",
        closeButton: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [addNotification, location]);

  useEffect(() => {
    if (location.pathname === INTERNAL_PATHS.SECONDARY_CHECKOUT) {
      getCheckoutData();
    }
  }, [location, getCheckoutData]);

  const handleOrderFailure = useCallback(() => {
    addNotification({
      message: FEEDBACK_MESSAGES.TIMEOUT,
      variant: "error",
      closeButton: false
    });
    setIsLoading(false);
    window.scrollTo(0, 0);

    setTimeout(() => {
      navigate(INTERNAL_PATHS.BUY_GIFT_CARDS);
    }, 3000);
  }, [navigate, addNotification]);

  const handleCheckout = useCallback(async () => {
    if (selectedPayment) {
      setIsLoading(true);
      try {
        const response = await checkoutRequest({ selectedPayment, device });

        const { data } = response;
        if (data.success && data.data.order_number) {
          const orderUrl = INTERNAL_PATHS.ORDERS.replace(
            ":orderId",
            data.data.order_number
          );
          emptyCart({ userID });
          window.location.assign(orderUrl);
          // navigate(orderUrl); TODO: Enable when secondary checkout is ready
        }
      } catch (err) {
        if (err.status === 422) return handleOrderFailure();

        const apiErrorMessage = err.response?.data?.errors?.[0];
        const errorMessage = apiErrorMessage
          ? mountCustomError(apiErrorMessage)
          : FEEDBACK_MESSAGES.CHECKOUT_ERROR;
        addNotification({
          message: errorMessage,
          variant: "error",
          closeButton: false
        });
        setIsLoading(false);
        window.scrollTo(0, 0);
      }
    }
  }, [
    selectedPayment,
    device,
    emptyCart,
    userID,
    addNotification,
    handleOrderFailure
  ]);

  const handleGcxCash = useCallback(
    async (isApplyingPoints) => {
      setIsLoading(true);

      try {
        const response = isApplyingPoints
          ? await applyPoints({ selectedPayment, device })
          : await removePoints();

        if (response.success) {
          await getCheckoutData();

          if (!isApplyingPoints) {
            addNotification({
              message: FEEDBACK_MESSAGES.REMOVE_POINTS_SUCCESS,
              variant: "success"
            });
          }
        }
      } catch (error) {
        const apiErrorMessage = error.response?.data?.errors?.[0];
        const defaultMessage = isApplyingPoints
          ? FEEDBACK_MESSAGES.APPLY_POINTS_ERROR
          : FEEDBACK_MESSAGES.REMOVE_POINTS_ERROR;

        const errorMessage = apiErrorMessage
          ? mountCustomError(apiErrorMessage)
          : defaultMessage;

        addNotification({
          message: errorMessage,
          variant: "error",
          closeButton: false
        });
        window.scrollTo(0, 0);
      } finally {
        setIsLoading(false);
      }
    },
    [device, selectedPayment, addNotification, getCheckoutData]
  );

  useEffect(() => {
    getCheckoutData();
  }, [getCheckoutData]);

  return {
    isLoading,
    checkoutData,
    selectedPayment,
    disableSubmitPayment,
    getCheckoutData,
    setPayment: setSelectedPayment,
    setDisableSubmitPayment,
    handleOrderFailure,
    handleCheckout,
    handleGcxCash
  };
};
